import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiArrowRight, FiUsers, FiTrendingUp, FiCamera, FiTarget, FiCalendar } = FiIcons;

const SocialMedia = () => {
  const services = [
    {
      title: "Gestione Social Completa",
      description: "Pianificazione, creazione contenuti e pubblicazione su tutti i canali social per la tua attività a Napoli",
      icon: FiUsers,
      features: ["Piano editoriale mensile", "Contenuti originali", "Pubblicazione programmata"]
    },
    {
      title: "Campagne Advertising",
      description: "Facebook Ads e Instagram Ads per aumentare reach, follower e conversioni reali",
      icon: FiTarget,
      features: ["Targeting preciso", "A/B testing", "Ottimizzazione ROI"]
    },
    {
      title: "Content Creation",
      description: "Foto, video, Stories e Reel coinvolgenti prodotti dal nostro team creativo",
      icon: FiCamera,
      features: ["Shooting fotografici", "Video editing", "Grafiche branded"]
    },
    {
      title: "Analytics & Report",
      description: "Monitoraggio costante delle performance e report dettagliati sui risultati ottenuti",
      icon: FiTrendingUp,
      features: ["KPI personalizzati", "Report mensili", "Insights strategici"]
    }
  ];

  const packages = [
    {
      name: "Social Starter",
      price: "€390/mese",
      description: "Perfetto per piccole attività locali",
      features: [
        "Fino a 2 social network",
        "8 post al mese",
        "Grafica personalizzata",
        "Gestione commenti",
        "Report mensile"
      ],
      popular: false
    },
    {
      name: "Social Business",
      price: "€590/mese",
      description: "Ideale per PMI che vogliono crescere",
      features: [
        "Fino a 3 social network",
        "12 post al mese",
        "Stories e Reel inclusi",
        "Community management",
        "Campagne ADS (gestione)",
        "Report dettagliato"
      ],
      popular: true
    },
    {
      name: "Social Enterprise",
      price: "€890/mese",
      description: "Per aziende con obiettivi ambiziosi",
      features: [
        "Fino a 4 social network",
        "16 post al mese",
        "Video e contenuti premium",
        "Campagne ADS avanzate",
        "Influencer marketing",
        "Strategia personalizzata"
      ],
      popular: false
    }
  ];

  return (
    <>
      <Helmet>
        <title>Social Media Manager Napoli – Gestione Facebook, Instagram e ADS</title>
        <meta name="description" content="Social Media Manager a Napoli: strategie social vincenti, creazione contenuti, campagne Facebook/Instagram Ads e gestione community. Fai decollare il tuo brand." />
        <meta name="keywords" content="social media manager napoli, gestione social napoli, facebook ads napoli, instagram marketing napoli, agenzia social napoli" />
        <link rel="canonical" href="https://realizzazionesitiwebnapoli.com/servizi/social-media-manager-napoli" />
      </Helmet>

      {/* Hero Section - Immagine Team Social/Marketing */}
      <section className="relative min-h-screen bg-napoli-blu text-white overflow-hidden flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1920&auto=format&fit=crop" 
            alt="Team Social Media Marketing Napoli" 
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          {/* Overlay moderno per esaltare il testo */}
          <div className="absolute inset-0 bg-gradient-to-r from-napoli-blu/95 via-napoli-blu/70 to-transparent"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-20 z-20">
          <div className="max-w-4xl mx-auto text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              className="inline-block bg-napoli-accent text-napoli-blu px-4 py-1 rounded-full font-bold text-sm mb-6 shadow-lg uppercase tracking-wider"
            >
              Social Media Strategy
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl lg:text-7xl font-poppins font-bold mb-6 text-white drop-shadow-2xl leading-tight"
            >
              Social Media <br/>
              <span className="text-napoli-accent text-shadow">Manager a Napoli</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl lg:text-2xl mb-10 text-white leading-relaxed drop-shadow-lg font-medium max-w-2xl"
            >
              Non postare a caso. Affidati a un team di esperti che trasforma i tuoi follower in clienti reali attraverso contenuti virali e campagne ADS mirate.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Link to="/contatti" className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-4 px-10 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl text-lg">
                Attiva i tuoi Social
              </Link>
              <a href="https://tidycal.com/sitiwebstudio/consulenza-siti-web-napoli" target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur-md hover:bg-white/20 text-white border border-white/30 font-bold py-4 px-10 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl text-lg">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 inline" /> Consulenza Gratuita
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-napoli-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-poppins font-bold text-napoli-blu mb-6">
              Servizi Social Media Completi
            </h2>
            <div className="w-24 h-1.5 bg-napoli-accent mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-medium">
              Dalla strategia alla pubblicazione, gestiamo tutto noi per farti risparmiare tempo e massimizzare i risultati.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white rounded-2xl p-10 hover:bg-napoli-blu transition-all duration-500 shadow-xl border border-napoli-azzurro/10"
              >
                <div className="w-16 h-16 bg-napoli-light rounded-xl flex items-center justify-center mb-8 shadow-md group-hover:bg-napoli-accent transition-colors duration-500">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-napoli-blu" />
                </div>
                <h3 className="text-2xl font-poppins font-bold text-napoli-blu mb-4 group-hover:text-white transition-colors duration-500">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-8 leading-relaxed font-medium group-hover:text-white/80 transition-colors duration-500">
                  {service.description}
                </p>
                <ul className="space-y-4">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-napoli-blu font-bold group-hover:text-white transition-colors duration-500">
                      <div className="w-6 h-6 bg-napoli-accent rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-napoli-blu" />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-poppins font-bold text-napoli-blu mb-6">
              Piani Social Media su Misura
            </h2>
            <p className="text-xl text-gray-600 font-medium">Scegli il livello di crescita per il tuo brand</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bg-white rounded-3xl p-10 shadow-2xl relative border-2 ${pkg.popular ? 'border-napoli-accent scale-105 z-10' : 'border-gray-100'}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                    <span className="bg-napoli-accent text-napoli-blu px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      RACCOMANDATO
                    </span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-poppins font-bold text-napoli-blu mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-napoli-azzurro mb-4">{pkg.price}</div>
                  <p className="text-gray-500 font-medium">{pkg.description}</p>
                </div>
                <ul className="space-y-4 mb-10">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/contatti" 
                  className={`block w-full text-center py-4 rounded-xl font-bold transition-all ${pkg.popular ? 'bg-napoli-accent text-napoli-blu hover:bg-yellow-400 shadow-xl' : 'bg-napoli-blu text-white hover:bg-napoli-dark'}`}
                >
                  Inizia ora
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1920&auto=format&fit=crop" 
            alt="Pianificazione Social Napoli" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-napoli-blu/90"></div>
        </div>
        
        <div className="relative container mx-auto px-4 text-center z-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl lg:text-6xl font-poppins font-bold mb-8 text-white">
              Porta la tua Community al livello successivo
            </h2>
            <p className="text-2xl mb-12 text-white/90 font-medium max-w-2xl mx-auto">
              Non restare invisibile. Le persone cercano la tua attività sui social ogni giorno: fatti trovare con il look giusto.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contatti" className="bg-napoli-accent hover:bg-yellow-400 text-napoli-blu font-bold py-5 px-12 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl text-xl">
                Richiedi un'Analisi Social
              </Link>
              <Link to="/portfolio" className="bg-white hover:bg-gray-100 text-napoli-blu font-bold py-5 px-12 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl text-xl">
                I Nostri Successi
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default SocialMedia;